import DOMPurify from "dompurify";
import axiosClient from "../AxiosClient";

// ===== Public API =====

// Latest published blogs as a plain list (home page)
const fetchLatestBlogs = (limit = 4) =>
  axiosClient.get("/blogs", { params: { limit } }).then((res) => res.data.blogs || []);

// Paginated published blogs: resolves to Laravel's paginator ({ data, current_page, last_page, ... })
const fetchBlogsPage = (page = 1, perPage = 9) =>
  axiosClient.get("/blogs", { params: { page, per_page: perPage } }).then((res) => res.data.blogs);

const fetchBlog = (slug) =>
  axiosClient.get(`/blogs/${encodeURIComponent(slug)}`).then((res) => res.data.blog);

// ===== Admin API =====

const adminFetchBlogs = (page = 1, search = "") =>
  axiosClient
    .get("/admin/blogs", { params: { page, search: search || undefined } })
    .then((res) => res.data.blogs);

const adminFetchBlog = (id) => axiosClient.get(`/admin/blogs/${id}`).then((res) => res.data.blog);

// `values` = { title, slug, summary, content, is_published, image (File | null) }
const toFormData = (values) => {
  const formData = new FormData();
  formData.append("title", values.title);
  formData.append("slug", values.slug || "");
  formData.append("summary", values.summary || "");
  formData.append("content", values.content);
  formData.append("is_published", values.is_published ? "1" : "0");
  if (values.image) formData.append("image", values.image);
  return formData;
};

const adminCreateBlog = (values) =>
  axiosClient
    .post("/admin/blogs", toFormData(values), { headers: { "Content-Type": "multipart/form-data" } })
    .then((res) => res.data);

// Sent as POST because PHP can't read files from multipart PUT requests
const adminUpdateBlog = (id, values) =>
  axiosClient
    .post(`/admin/blogs/${id}`, toFormData(values), { headers: { "Content-Type": "multipart/form-data" } })
    .then((res) => res.data);

const adminTogglePublish = (id) => axiosClient.patch(`/admin/blogs/${id}/publish`).then((res) => res.data);

const adminDeleteBlog = (id) => axiosClient.delete(`/admin/blogs/${id}`).then((res) => res.data);

// ===== Helpers =====

const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatBlogDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

// The editor (Quill 2) stores every list as <ol> with <li data-list="bullet|ordered">.
// Rebuild real <ul>/<ol> lists so posts read correctly outside the editor.
const rebuildEditorLists = (root) => {
  root.querySelectorAll("li > .ql-ui").forEach((el) => el.remove());

  root.querySelectorAll("ol").forEach((list) => {
    const items = Array.from(list.children);
    if (!items.some((li) => li.hasAttribute("data-list"))) return;

    const fragment = document.createDocumentFragment();
    let current = null;
    let currentType = null;

    items.forEach((li) => {
      const type = li.getAttribute("data-list") === "bullet" ? "ul" : "ol";
      if (type !== currentType) {
        current = document.createElement(type);
        fragment.appendChild(current);
        currentType = type;
      }
      li.removeAttribute("data-list");
      current.appendChild(li);
    });

    list.replaceWith(fragment);
  });
};

// Sanitises blog HTML (the backend cleans it too) and fixes editor list markup
const prepareBlogHtml = (html) => {
  const clean = DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: [
      "p", "br", "h1", "h2", "h3", "h4", "strong", "b", "em", "i", "u", "s",
      "ul", "ol", "li", "a", "blockquote", "pre", "code", "img", "span",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "class", "data-list"],
  });

  const container = document.createElement("div");
  container.innerHTML = clean;
  rebuildEditorLists(container);
  container.querySelectorAll("a[href^='http']").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  });
  return container.innerHTML;
};

export {
  fetchLatestBlogs,
  fetchBlogsPage,
  fetchBlog,
  adminFetchBlogs,
  adminFetchBlog,
  adminCreateBlog,
  adminUpdateBlog,
  adminTogglePublish,
  adminDeleteBlog,
  slugify,
  formatBlogDate,
  prepareBlogHtml,
};
